import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ProtoApiService } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import {
    Approval,
    CreditMemo,
    CreditMemoAllocation,
    EnumInput,
    Invoice,
    ListInvoicePayLaterEligibleForBuyerRequest,
    ListInvoicePayLaterEligibleForBuyerResponse,
    mapBuyerPayableSortColumn,
    PayablesInput,
    QueryFields,
    SortDirection
} from '@lsq-frontend/remote-services/financing-graphql';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { isNil, omitBy } from 'lodash';
import { IExportRequest } from '../models/export-request';
import { ExportType } from '../models/export-type';
import { Money } from '../../../../financing-graphql/src/lib/model/models';

@Injectable({ providedIn: 'root' })
export class PayLaterInvoiceExportService extends ProtoApiService<
    ListInvoicePayLaterEligibleForBuyerRequest,
    ListInvoicePayLaterEligibleForBuyerResponse
> {
    public endpointName = 'v2/financingExport';
    public serviceName = 'reportCenter';
    public usePascalForUrl = false;

    public requestType = ListInvoicePayLaterEligibleForBuyerRequest;
    public responseType = ListInvoicePayLaterEligibleForBuyerResponse;

    constructor(http: HttpClient, @Inject(SETTINGS) settings: ISettings) {
        super(http, settings);
    }

    public requestMapper(req: ListInvoicePayLaterEligibleForBuyerRequest): IExportRequest {
        const { skip, take, sortColumn, sortDirection } = req.pagination;
        const statuses = req.buyerInvoiceStatus.map((status) => {
            return new EnumInput(status);
        });
        const input: PayablesInput = omitBy(
            {
                companyFilter: {
                    buyerIds: [req.refBuyerKey],
                    supplierIds: req.sellerKeys ?? []
                },
                payableNameSearch: req.payableNameSearch ?? '',
                dueDateRange: omitBy(req.dueDateRange, isNil),
                statuses: statuses,
                pagination: {
                    skip: skip || 0,
                    take: take || 0,
                    sortColumn: new EnumType(mapBuyerPayableSortColumn(sortColumn)),
                    sortDirection: new EnumType(
                        SortDirection[sortDirection] ?? SortDirection.DESCENDING
                    )
                }
            },
            isNil
        );
        const money = { cents: true, currencyCode: true };
        const invoice: QueryFields<Invoice> = {
            date: true,
            terms: true,
            invoiceNumber: true,
            amount: money
        };
        const creditMemos: QueryFields<CreditMemo> = {
            creditMemoNumber: true,
            date: true,
            amount: money,
            allocatedAmount: money
        };
        const allocations: QueryFields<CreditMemoAllocation> = {
            id: true,
            approvalId: true,
            allocatedAmount: money,
            creditMemoId: true,
            creditMemo: creditMemos,
            allocationtype: true
        };
        const approvals: QueryFields<Approval> = {
            invoice,
            originalDueDate: true,
            estimatedPayLaterAdjustedDueDate: true,
            estimatedPayLaterTotal: money,
            estimatedPayLaterFee: money,
            creditMemoAllocations: allocations,
            adjustedAmount: money
        };
        const query = jsonToGraphQLQuery({
            payables: {
                __args: { input },
                rows: {
                    id: true,
                    name: true,
                    amount: money,
                    invoiceDateParent: true,
                    originalDueDate: true,
                    estimatedPayLaterAdjustedDueDate: true,
                    estimatedPayLaterFee: money,
                    dueInDays: true,
                    estimatedPayLaterTotal: money,
                    isGroup: true,
                    termsExtensionDays: true,
                    payLaterFeeRate: true,
                    payLaterEligible: true,
                    relationship: {
                        supplier: { company: { name: true } }
                    },
                    approvals,
                    creditMemos
                }
            }
        });

        return {
            fileType: 'csv',
            query: `{${query}}`,
            exportType: ExportType.BUYER_PAY_LATER
        };
    }

    public get endpointUrl(): string {
        return super.endpointUrl.replace('/api', '');
    }
}
